import { RouteConfigInstance } from "../../projects/web/src/app/routes.config";

/** Single key or list — list means user needs ANY one (OR). */
export type PermissionKey = string;
export type PermissionKeys = PermissionKey | PermissionKey[];

/** Macro section `.edit` keys (3 dot segments) are code-only, not stored in DB. */
const MACRO_EDIT_KEY =
  /^section\.[^.]+\.edit$/;

export function isMacroSectionEditKey(key: string): boolean {
  return MACRO_EDIT_KEY.test(key);
}

export function normalizePermissionKeys(
  keys?: PermissionKeys | PermissionKeys[] | null
): string[] {
  if (keys == null) {
    return [];
  }
  const flat = Array.isArray(keys) ? keys : [keys];
  const out: string[] = [];
  for (const item of flat) {
    if (Array.isArray(item)) {
      out.push(...item.filter(Boolean));
    } else if (typeof item === "string" && item) {
      out.push(item);
    }
  }
  return [...new Set(out)];
}

function flatMapPermissionKeys(
  items: string[],
  mapFn: (item: string) => string[]
): string[] {
  const out: string[] = [];
  for (const item of items) {
    out.push(...mapFn(item));
  }
  return out;
}

/** Legacy flat token → WS-2403 read/write keys for route guards during migration. */
const LEGACY_ROUTE_MAP: Record<
  string,
  { read: PermissionKeys; write: PermissionKeys }
> = {
  DASHBOARD: {
    read: ["DASHBOARD", "section.dashboard.view"],
    write: ["WRITE", "section.dashboard.edit"],
  },
  CALENDAR: {
    read: ["CALENDAR", "section.calendar.view"],
    write: ["WRITE", "section.calendar.edit"],
  },
  CRM: {
    read: ["CRM", "section.crm.view"],
    write: ["WRITE", "section.crm.edit"],
  },
  ORDERS: {
    read: ["ORDERS", "section.sales.view"],
    write: ["WRITE", "section.sales.edit"],
  },
  MARKETING: {
    read: ["MARKETING", "section.marketing.view"],
    write: ["WRITE", "section.marketing.edit"],
  },
  WINERY_ADMIN: {
    read: ["WINERY_ADMIN"],
    write: ["WINERY_ADMIN", "WRITE"],
  },
  USER: {
    read: ["USER", "BASIC_LOGGED_USER"],
    write: ["WRITE"],
  },
};

function sectionBaseReadWrite(sectionBase: string): {
  read: string[];
  write: string[];
} {
  return {
    read: [`${sectionBase}.view`, `${sectionBase}.edit`],
    write: ["WRITE", `${sectionBase}.edit`],
  };
}

export function resolveReadPermissions(route: RouteConfigInstance): string[] {
  const explicit = normalizePermissionKeys(route.readPermission);
  const legacyPerm = normalizePermissionKeys(route.permission);
  const fromLegacy = flatMapPermissionKeys(legacyPerm, (p) =>
    normalizePermissionKeys(LEGACY_ROUTE_MAP[p]?.read ?? p)
  );
  const fromSection = route.permissionSectionBase
    ? sectionBaseReadWrite(route.permissionSectionBase).read
    : [];
  return [...new Set([...explicit, ...fromLegacy, ...fromSection])];
}

export function resolveWritePermissions(route: RouteConfigInstance): string[] {
  const explicit = normalizePermissionKeys(route.writePermission);
  const legacyPerm = normalizePermissionKeys(route.permission);
  const fromLegacy = flatMapPermissionKeys(legacyPerm, (p) =>
    normalizePermissionKeys(LEGACY_ROUTE_MAP[p]?.write ?? "WRITE")
  );
  const fromSection = route.permissionSectionBase
    ? sectionBaseReadWrite(route.permissionSectionBase).write
    : [];
  return [...new Set([...explicit, ...fromLegacy, ...fromSection])];
}
